import React from 'react';

import type { EventAlertDialogProps } from '@looko-design-system';
import { atom, useAtom } from 'jotai';
import { isEmpty } from 'lodash-es';

import type { PartialSome } from '@looko-corp/acloset-react-kit';

import { EventModalDialog } from './Modal/EventModalDialog';

type ModalDialogType = Omit<EventAlertDialogProps, 'open'>;

const globalModalDialogDefaultValues: ModalDialogType = {
  content: undefined,
  confirmButtonText: '확인',
  confirmButtonColor: undefined,
  cancelButtonText: '취소',
  onClose: () => {},
  onConfirm: () => true,
};

const globalModalDialogAtom = atom<ModalDialogType[]>([]);

/**
 * 글로벌에 정의한 모달 컴포넌트입니다.
 */
export function GlobalModalDialog() {
  const [dialogs, setDialogs] = useAtom(globalModalDialogAtom);

  const currentDialog = dialogs[0];

  const handleClose = React.useCallback(() => {
    setDialogs([...dialogs.slice(1)]);
    currentDialog?.onClose?.();
  }, [currentDialog, dialogs, setDialogs]);

  if (isEmpty(dialogs)) {
    return null;
  }
  return (
    <EventModalDialog
      {...currentDialog}
      open={currentDialog != null}
      onClose={handleClose}
      maxWidth={currentDialog?.maxWidth ?? 'xs'}
    />
  );
}

/**
 * 글로벌에 정의해놓은 모달을 켜고 끄기 위한 훅을 가져옵니다.
 * @returns openDialog : 모달 다이얼로그를 켭니다.
 * @returns resetDialog : 전체 모달 다이얼로그를 끕니다.
 */
export function useGlobalModalDialog() {
  const [dialogs, setDialogs] = useAtom(globalModalDialogAtom);

  const openDialog = React.useCallback(
    (values: PartialSome<ModalDialogType, 'onConfirm'>) => {
      setDialogs([...dialogs, { ...globalModalDialogDefaultValues, ...values }]);
    },
    [dialogs, setDialogs],
  );
  const resetDialog = React.useCallback(() => {
    setDialogs([]);
  }, [setDialogs]);

  return { openDialog, resetDialog };
}
